/* eslint-disable no-console */
import { updateKeywordEvents } from '~/actions/keyword_events';
import { setCollection } from '~/actions/channel_manager';
import { setEnterprise } from '~/actions/enterprise';
import { getStore } from '~/store/helpers';

import {
  setUserData,
  deleteMessages,
  fetchMe,
  setMessages,
  addConversation,
  updateConversationsWithRemovedChannel,
  setEchoAgent,
  setEchoChannel,
} from '~/thunks';

import { createConversationReducer } from '~/reducers/conversations';

import {
  HUB_MESSAGE_TYPE,
  SERVER_ORIGIN_WS_EVENTS,
  MessageStatus,
  Origin,
  CLIENT_ORIGIN_WS_EVENTS,
  ChannelStatus,
  ChannelType,
} from '~/constants';

import { setIsSocketConnected } from '~/actions/global';
import { setTemplates } from '~/actions/media_storage';
import { setUnreadMessages } from '~/actions/unread_messages';

import { getPrimus, initPrimus } from '~/utils/primus';
import { setNotificationStatus } from '~/actions/notificationStatus';
import {
  updateConversation,
  addChannelTypesToConversations,
} from '~/actions/conversations';

import { MESSAGES_CHUNK_SIZE } from '../constants';

const ensureSocketConnection = (store) => {
  const { isSocketConnected } = store.getState().global;

  if (!isSocketConnected) store.dispatch(setIsSocketConnected(true));
};

const Coll = async (coll, res, enqueueSnackbar) => {
  const store = getStore();

  const { data = [], ...rest } = res;

  if (coll === 'agents') {
    // we need agents data for customer channelTypes badly
    ensureSocketConnection(store);

    store.dispatch(setCollection({ coll, data }));

    const {
      global: { echoAgentId },
    } = store.getState();

    if (!echoAgentId) store.dispatch(setEchoAgent());

    // in data we have only 1 patched sms agent
    if (
      data.length === 1 &&
      data[0].channel_type === ChannelType.SMS &&
      data[0].status === ChannelStatus.ACTIVE
    ) {
      store.dispatch(
        addChannelTypesToConversations({ channelType: data[0].channel_type })
      );
    }
  }

  if (coll === 'keyword_events') {
    store.dispatch(updateKeywordEvents(data));
  }

  if (coll === 'users') {
    store.dispatch(setUserData(data));
  }

  if (coll === 'channels') {
    store.dispatch(setCollection({ coll, data }));

    const {
      global: { echoChannelId },
    } = store.getState();

    if (!echoChannelId) store.dispatch(setEchoChannel());

    // in data we have only 1 removed sms channel
    if (data.length === 1 && data[0].status === ChannelStatus.REMOVED) {
      const channel = data[0];

      store.dispatch(updateConversationsWithRemovedChannel(channel.type));
    }
  }

  if (coll === 'customers') {
    const primus = getPrimus();

    const {
      channel_manager: { agents, channels },
    } = store.getState();

    data.forEach((customer) => {
      store.dispatch(addConversation(customer));

      const itemID = customer.id || customer._id;

      store.reducerManager.add(
        `conversation_${itemID}`,
        createConversationReducer(itemID, agents, channels)
      );

      const isNotified = Object.values(
        customer.chatbot_notification_received
      ).some(Boolean);

      store.dispatch(
        setNotificationStatus({
          customer_id: customer.id,
          isNotified,
        })
      );
    });

    if (data.length === 1) {
      primus.emit(CLIENT_ORIGIN_WS_EVENTS.COLLECTION, {
        cid: 'messages',
        limit: MESSAGES_CHUNK_SIZE,
        sort: 'sent',
        order: -1,
        filters: { customer_id: data[0].id || data[0]._id },
      });
    }
  }

  if (coll === 'messages') {
    if (data.length > 0) {
      const { customer_id } = data[0];

      const {
        channel_manager: { agents },
        unread_messages,
      } = store.getState();

      const { type } = rest;

      if (type === HUB_MESSAGE_TYPE.DELETE_RESOURCE) {
        store.dispatch(
          deleteMessages(
            customer_id,
            data.map((message) => message.id)
          )
        );

        return;
      }

      store.dispatch(
        setMessages(customer_id, { messages: data, agents, ...rest })
      );

      if (type === HUB_MESSAGE_TYPE.GET_RESPONSE) {
        const unreadMessages = data.reduce(
          (sum, message) =>
            message.status !== MessageStatus.READ &&
            message.origin === Origin.CUSTOMER
              ? sum + 1
              : sum,
          0
        );

        if (unreadMessages > 0 || unread_messages[customer_id] > 0) {
          store.dispatch(
            setUnreadMessages({
              customer_id,
              unread: unreadMessages,
            })
          );
        }
      }
    }
  }

  if (coll === 'enterprises') {
    const [enterprise] = data;
    const state = store.getState();

    store.dispatch(setEnterprise(enterprise));

    const apiKey = state?.user?.config?.mw_api_key;
    const isPooling = state?.user?.isPooling ?? false;

    // starts pooling sequence
    if (apiKey !== undefined && !isPooling) {
      store.dispatch(fetchMe({ enqueueSnackbar }));
    }
  }

  if (coll === 'templates') {
    store.dispatch(setTemplates(data));
  }
};

/**
 * @param options {{
 *   enqueueSnackbar: Function
 *   apiBaseURL: string
 * }}
 * @returns {Promise<unknown>}
 */
export const connect = async ({ enqueueSnackbar, apiBaseURL } = {}) =>
  new Promise((resolve, reject) => {
    initPrimus({ apiBaseURL });

    const primus = getPrimus();
    const store = getStore();

    primus
      .on('open', () => primus.emit('auth'))
      .on('auth', (res) => {
        if (res.status === 200) {
          primus.emit(CLIENT_ORIGIN_WS_EVENTS.COLLECTION, {
            cid: 'enterprises',
          });
          primus.emit(CLIENT_ORIGIN_WS_EVENTS.COLLECTION, {
            cid: 'agents',
            limit: 1000,
          });
          primus.emit(CLIENT_ORIGIN_WS_EVENTS.COLLECTION, {
            cid: 'channels',
            limit: 1000,
          });
          primus.emit(CLIENT_ORIGIN_WS_EVENTS.COLLECTION, {
            cid: 'messages',
            order: -1,
            limit: 20,
          });
          primus.emit(CLIENT_ORIGIN_WS_EVENTS.COLLECTION, {
            cid: 'customers',
            order: -1,
            limit: 20,
          });

          return resolve(res);
        }

        return reject(res);
      })
      .on(SERVER_ORIGIN_WS_EVENTS.COLLECTION, (coll, res) =>
        Coll(coll, res, enqueueSnackbar)
      )
      .on(
        SERVER_ORIGIN_WS_EVENTS.CHATBOT_NOTIFICATION,
        ({ customer = {}, scenarioName = '', isNotified = false }) => {
          store.dispatch(
            setNotificationStatus({
              customer_id: customer._id,
              isNotified,
            })
          );

          if (isNotified) {
            const message = `Customer ${
              customer.name || customer.phone
            } has reached the final point in Chatbot ${scenarioName} scenario`;

            enqueueSnackbar(message, {
              preventDuplicate: true,
              variant: 'warning',
            });
          }
        }
      )
      .on('notify', ({ message, variant = 'warning' }) => {
        enqueueSnackbar(message, { preventDuplicate: true, variant });
      })
      .on('end', (data) => console.log('Socket is end', data))
      .on('close', (data) => console.log('Socket is open', data))
      .on('err', (...err) => {
        console.error('sys err', ...err);
        enqueueSnackbar(err[0].message, {
          preventDuplicate: true,
          variant: 'error',
        });
      })
      .on('online', () => console.log('Socket is online'))
      .on('offline', () => console.error('Socket offline'))
      .on('event', (eventName, data) => {
        if (eventName === 'IS_TYPING') {
          const dispatchCustomer = (is_typing) => {
            store.dispatch(
              updateConversation({
                conversationID: data.customer_id,
                is_typing,
              })
            );
          };

          dispatchCustomer(true);
          setTimeout(() => {
            dispatchCustomer(false);
          }, 20000);
        }
      });
  });

export * from './queries';
